import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Socket } from "socket.io-client";

// ─── Socket Context ───────────────────────────────────────────────────────────

type SocketProviderProps = { socket: typeof Socket | undefined; children: ReactNode };
const socketContext = createContext<Socket | undefined>(undefined);

export function useSocket() { return useContext(socketContext); }

export function SocketProvider({ socket, children }: SocketProviderProps) {
    return <socketContext.Provider value={socket as any}>{children}</socketContext.Provider>;
}

// ─── Notification Context ─────────────────────────────────────────────────────

interface NotificationState {
    notifications: any[];
    unreadCount: number;
    totalCount: number;
}

interface NotificationContextValue {
    notifyInfo: NotificationState;
    loadingMore: boolean;
    lastReceivedAt: number;
    fetchNotifications: (p?: number) => Promise<void>;
    handleSeeMore: () => Promise<void>;
    prependNotification: (n: any) => void;
    markRead: (id: number) => void;
    markUnread: (id: number) => void;
    markAllRead: () => void;
}

const LIMIT = 10;
const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function useNotifications() {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error("useNotifications must be used inside NotificationProvider");
    return ctx;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [notifyInfo, setNotifyInfo] = useState<NotificationState>({
        notifications: [],
        unreadCount: 0,
        totalCount: 0,
    });
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastReceivedAt, setLastReceivedAt] = useState(0);

    const fetchNotifications = async (p = 1) => {
        const data = await fetch(`/api/notifications?page=${p}&limit=${LIMIT}`).then(r => r.json());
        if (!data.success) return;
        if (p === 1) {
            setNotifyInfo({ notifications: data.notifications, unreadCount: data.unreadCount, totalCount: data.totalCount });
        } else {
            setNotifyInfo(prev => ({
                notifications: [...prev.notifications, ...data.notifications],
                unreadCount: data.unreadCount,
                totalCount: data.totalCount,
            }));
        }
    };

    const handleSeeMore = async () => {
        const next = page + 1;
        setLoadingMore(true);
        await fetchNotifications(next);
        setPage(next);
        setLoadingMore(false);
    };

    const prependNotification = (n: any) => {
        setNotifyInfo(prev => ({
            notifications: [n, ...prev.notifications],
            unreadCount: prev.unreadCount + 1,
            totalCount: prev.totalCount + 1,
        }));
        setLastReceivedAt(Date.now());
    };

    const markRead = (id: number) => {
        setNotifyInfo(prev => {
            const notifications = prev.notifications.map(n => n.id === id ? { ...n, isRead: true } : n);
            return { ...prev, notifications, unreadCount: notifications.filter(n => !n.isRead).length };
        });
    };

    const markUnread = (id: number) => {
        setNotifyInfo(prev => {
            const notifications = prev.notifications.map(n => n.id === id ? { ...n, isRead: false } : n);
            return { ...prev, notifications, unreadCount: notifications.filter(n => !n.isRead).length };
        });
    };

    const markAllRead = () => {
        setNotifyInfo(prev => ({
            ...prev,
            notifications: prev.notifications.map(n => ({ ...n, isRead: true })),
            unreadCount: 0,
        }));
    };

    useEffect(() => {
        fetchNotifications(1);
    }, []);

    return (
        <NotificationContext.Provider value={{ notifyInfo, loadingMore, lastReceivedAt, fetchNotifications, handleSeeMore, prependNotification, markRead, markUnread, markAllRead }}>
            {children}
        </NotificationContext.Provider>
    );
}
